import { useTranslation } from 'react-i18next';
import { iconLanguageSelector } from '../../assets';
import { useTheme } from '../../theme';
import { LANGUAGE_STORAGE_KEY } from '../../i18n';

export type Language = 'en-US' | 'es';

export interface LanguageSelectorProps {
  /** Idioma atualmente selecionado */
  selectedLanguage?: Language;
  /** Callback quando o idioma é alterado */
  onChange?: (language: Language) => void;
  /** Cor de fundo do idioma selecionado (ex.: mesma do botão Login — branding.primaryColor) */
  primaryColor?: string;
  /** Identificador de teste para o container do seletor */
  dataE2e?: string;
}

export default function LanguageSelector({
  selectedLanguage,
  onChange,
  primaryColor,
  dataE2e,
}: LanguageSelectorProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const selectedBg = primaryColor ?? theme.palette.primary.main;
  const containerBg = theme.palette.text.white;

  const currentLanguage = selectedLanguage || (i18n.language as Language);

  const handleLanguageChange = (language: Language) => {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // ignore
    }
    void i18n.changeLanguage(language);
    onChange?.(language);
  };

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0,
    backgroundColor: containerBg,
    borderRadius: theme.shape.borderRadius.full,
    padding: theme.spacing.md,
    fontFamily: theme.typography.fontFamily,
    boxShadow: 'none',
    ['--language-selector-selected-bg' as string]: selectedBg,
  };

  const iconStyle: React.CSSProperties = {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.primary,
    fontSize: '20px',
    margin: '8px',
  };

  const buttonBaseStyle: React.CSSProperties = {
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.md,
    fontWeight: theme.typography.fontWeight.bold,
    padding: '10px 24px',
    minWidth: '64px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none',
  };

  const getButtonStyle = (language: Language): React.CSSProperties => {
    const isSelected = currentLanguage === language;
    return {
      ...buttonBaseStyle,
      backgroundColor: isSelected ? selectedBg : containerBg,
      color: isSelected ? theme.palette.text.white : theme.palette.text.primary,
      borderRadius: theme.shape.borderRadius.full,
    };
  };

  return (
    <div style={containerStyle} data-e2e={dataE2e}>
      <div style={iconStyle}>
        {/* <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M2 12h20M12 2c-2.5 3-2.5 9 0 20M12 2c2.5 3 2.5 9 0 20"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
        </svg> */}
        <img
          src={iconLanguageSelector}
          alt="Language Selector"
          style={iconStyle}
        />
      </div>
      <button
        type="button"
        className={`e-learning-button language-selector-btn ${currentLanguage === 'en-US' ? 'language-selector-btn--selected' : ''}`}
        onClick={() => handleLanguageChange('en-US')}
        style={getButtonStyle('en-US')}
        aria-label="English"
      >
        EN
      </button>
      <button
        type="button"
        className={`e-learning-button language-selector-btn ${currentLanguage === 'es' ? 'language-selector-btn--selected' : ''}`}
        onClick={() => handleLanguageChange('es')}
        style={getButtonStyle('es')}
        aria-label="Spanish"
      >
        ES
      </button>
    </div>
  );
}
